import React, { useState } from 'react';
import { Input, Button, Alert, Form } from 'antd';
import styles from './security.module.css';
import { useMutation } from '@apollo/client';
import { UPDATE_PROJECT } from '../../queries';
import { CircleCheckBig, Edit, CircleX } from 'lucide-react';
interface Props {
  project: {
    id: string;
    basicAuthUsername: string;
    basicAuthPassword: string;
    organizationId: string;
  };
  onCancel?: () => void;
  onSave?: () => void;
  accessTo: {
    mobileScreenshots: boolean;
  };
  refetch?: () => void;
}
const Security: React.FC<Props> = (props: Props) => {
  const {
    project,
    project: { basicAuthUsername, basicAuthPassword },
    accessTo,
    refetch
  } = props;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBasicAuthForm, setShowBasicAuthForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [updateProject] = useMutation(UPDATE_PROJECT, {
    onCompleted: () => {
      setSaving(false);
      setError(null);
      setShowBasicAuthForm(false);
      if (refetch) {
        refetch();
      } else {
        window.location.reload();
      }
    },
    onError: error => {
      setSaving(false);
      // Display the error message directly from GraphQL
      const errorMessage =
        error.graphQLErrors?.[0]?.message ||
        error.message ||
        'Something went wrong while saving your basic authentication settings. Please try again.';
      setError(errorMessage);
    }
  });

  const submit = () => {
    setSaving(true);
    setError(null);

    const { getFieldValue } = form;
    const body = {
      basicAuthUsername: getFieldValue('basicAuthUsername') || null,
      basicAuthPassword: getFieldValue('basicAuthPassword') || null
    };

    updateProject({
      variables: {
        projectId: project.id,
        ...body
      }
    });
  };

  const handleCancel = () => {
    props.onCancel();
    // This is because antd animates the modal, so if you reset NOW, the user sees.
    setTimeout(() => {
      form.resetFields();
    }, 500);
  };

  const handleRemove = () => {
    setSaving(true);
    setError(null);

    updateProject({
      variables: {
        projectId: project.id,
        basicAuthUsername: null,
        basicAuthPassword: null
      }
    });
  };

  const hasBasicAuthInfo = basicAuthUsername && basicAuthPassword;

  const renderBasicAuthForm = () => (
    <Form
      form={form}
      onFinish={submit}
      className={styles.form}
      layout="vertical"
      hideRequiredMark
    >
      {!accessTo.mobileScreenshots && (
        <Alert
          className={styles.alert}
          type="info"
          showIcon
          closable={false}
          message={
            <p className={styles.alertMessage}>
              This screenshot feature is not available on your current plan. On
              a higher plan, BugHerd will capture accurate screenshots, on
              desktop and mobile, on Safari, Chrome, Edge (IE), and Firefox.
              <a
                href={`/organizations/${project.organizationId}/subscription`}
                className={styles.upgradeAnchor}
              >{` Upgrade here`}</a>
              .
            </p>
          }
        />
      )}
      <Form.Item
        label={<span className={styles.basicInputLabel}>Username:</span>}
        name="basicAuthUsername"
        initialValue={basicAuthUsername}
        rules={[
          {
            required: true,
            message: 'Username is required'
          },
          () => ({
            validator(rule, value) {
              if (
                !value ||
                (!value.includes('#') &&
                  !value.includes('@') &&
                  !value.includes(':'))
              ) {
                return Promise.resolve();
              }

              return Promise.reject('Username cannot contain # @ or :');
            }
          })
        ]}
      >
        <Input
          name="project[basic_auth_username]"
          autoComplete="off"
          data-lpignore="true"
        />
      </Form.Item>
      <Form.Item
        label={<span className={styles.basicInputLabel}>Password:</span>}
        name="basicAuthPassword"
        initialValue={basicAuthPassword}
        rules={[
          {
            required: true,
            message: 'Password is required'
          },
          () => ({
            validator(rule, value) {
              if (
                !value ||
                (!value.includes('#') &&
                  !value.includes('@') &&
                  !value.includes(':'))
              ) {
                return Promise.resolve();
              }

              return Promise.reject('Password cannot contain # @ or :');
            }
          })
        ]}
      >
        <Input.Password
          name="project[basic_auth_password]"
          autoComplete="off"
          data-lpignore="true"
        />
      </Form.Item>
      <div className={styles.actions}>
        <Button
          className={styles.button}
          type="primary"
          htmlType="submit"
          loading={saving}
        >
          {saving ? 'Checking...' : 'Save'}
        </Button>
        {props.onCancel && (
          <Button className={styles.ghost} onClick={handleCancel}>
            Cancel
          </Button>
        )}
      </div>
    </Form>
  );

  const renderActiveBasicAuthDetails = () => (
    <div className={styles.activeBasicAuthContainer}>
      <span className={styles.activeLabel}>
        <CircleCheckBig className={styles.checkIcon} />
        ACTIVE
      </span>
      <div className={styles.basicAuthDetails}>
        <span>
          <strong>Username: </strong>
          {basicAuthUsername}
        </span>
        <span
          title="Click to view password"
          onClick={() => setShowPassword(true)}
        >
          <strong>Password: </strong>
          {showPassword ? basicAuthPassword : '**************'}
        </span>
      </div>
      <div className={styles.actions}>
        <Button
          type="text"
          className={styles.editButton}
          onClick={() => setShowBasicAuthForm(true)}
          icon={<Edit className={styles.editIcon} />}
        >
          Edit
        </Button>
        <Button
          type="text"
          className={styles.removeButton}
          onClick={handleRemove}
          icon={<CircleX className={styles.editIcon} />}
        >
          Remove
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.security}>
      <h3 className={styles.basicHeader}>Basic Authentication</h3>
      <p className={styles.basicText}>
        To capture screenshots behind Basic Authentication, add your credentials
        below.
      </p>
      {error && (
        <Alert
          className={styles.alert}
          type="error"
          showIcon
          onClose={() => setError(null)}
          message={error}
        />
      )}
      {!hasBasicAuthInfo || showBasicAuthForm
        ? renderBasicAuthForm()
        : hasBasicAuthInfo && renderActiveBasicAuthDetails()}
    </div>
  );
};

export default Security;
